import { BuildClass, DateTimeObj, guid, runPromisesSequentially, TW_OVERFLOW_ELLIPSIS, } from '../../../../universal';
import { _, React } from '../../../lib';
import { Button } from '../buttons';
import { ChatBasic } from '../chat-basic';
import { AgentUI, getChatCSSVars, getPendingChangeStatus, hasPendingApproval, MarkdownRenderer, PendingChangesTray, } from '../chat';
import { Checkbox } from '../checkbox';
import { PromptConfirm } from '../confirm';
import { setContextMenu } from '../context-menu';
import { GridCell } from '../editable-cells';
import { EditableGrid } from '../editable-grid';
import { FormComponent, FormType, openFlyoutForm } from '../form';
import { List } from '../list';
import { useRSInstance } from '../meta-types';
import { NoteAttachment } from '../notes';
import { ResizeSplitter } from '../resize-split';
import { SidebarListGrid } from '../sidebar-listgrid';
import { TabControl } from '../tabs';
import { searchText, StubText } from '../text';
import { TextareaWithVoice } from '../text-with-voice';
import { Textbox } from '../textbox';
import { ToolbarNew } from '../toolbar';
import { UsageLimitDonut } from '../usage-limit-donut';
import { SYNTHETIC_ROW_IDS, } from './types';
export * from './types';
const STATUS_LABELS = {
    50: 'Archived',
    80: 'Unapproved',
    100: 'Pending',
    149: 'Categorising',
    150: 'Categorised',
    199: 'Processing',
    200: 'Collaboration',
    601: 'Questions',
    602: 'Partial Approval',
    603: 'Full Approval',
    900: 'Completed',
};
const TASK_NOTE_MAX_LENGTH = 50000;
const getDefaultState = () => ({
    selectedRowID: SYNTHETIC_ROW_IDS.DASHBOARD,
    currentTab: 'overview',
    sidebarSearchText: '',
    currentSettingsTab: 'email',
    currentQuickOnlyTab: 'chat',
    instructionDrafts: {
        user: '',
        'categoriser-general': '',
        'categoriser-attachments': '',
        'categoriser-summaries': '',
        'categoriser-relevance': '',
    },
    showUnapproved: false,
    showArchived: false,
    draftPrimaryNote: '',
    draftSecondaryNote: '',
    newReceiverName: '',
    pendingChangesStates: {},
    attachmentSplitWidths: null,
    mainSplitWidths: null,
    taskSplitWidths: null,
    taskActionErrors: {},
    runningCategoriserTaskIDs: [],
});
export const AgentConsole = (props) => {
    const rs = useRSInstance({
        props,
        defaultState: getDefaultState,
        refs: {},
        actionToDelta: null,
    });
    const syntheticRows = [
        {
            type: 'synthetic',
            ID: SYNTHETIC_ROW_IDS.DASHBOARD,
            label: 'Dashboard',
            icon: 'dashboard',
        },
        {
            type: 'synthetic',
            ID: SYNTHETIC_ROW_IDS.QUICK_AGENTS,
            label: 'Quick Agents',
            icon: 'quick-agents',
        },
        {
            type: 'synthetic',
            ID: SYNTHETIC_ROW_IDS.AUTOMATIONS,
            label: 'Automations',
            icon: 'automations',
        },
        {
            type: 'synthetic',
            ID: SYNTHETIC_ROW_IDS.SETTINGS,
            label: 'Settings',
            icon: 'settings',
        },
        {
            type: 'synthetic',
            ID: SYNTHETIC_ROW_IDS.HELP,
            label: 'Help',
            icon: 'help',
        },
    ];
    const filteredTasks = _.chain(props.data.tasks)
        .filter(task => {
        if (!rs.state.showUnapproved && task.Status === 80) {
            return false;
        }
        if (!rs.state.showArchived && task.Status === 50) {
            return false;
        }
        return true;
    })
        .value();
    const taskRows = filteredTasks.map(task => ({
        type: 'task',
        data: task,
    }));
    const sidebarData = _.chain([...syntheticRows, ...taskRows])
        .filter(row => searchText(row.type === 'synthetic'
        ? row.label
        : (row.data.Title ?? 'Untitled Task'), rs.state.sidebarSearchText))
        .value();
    const getRowID = (row) => row.type === 'synthetic' ? row.ID : row.data.ID;
    const renderMainContent = () => {
        const selectedID = rs.state.selectedRowID;
        if (!selectedID) {
            return (React.createElement("div", { className: "flex-1 flex items-center justify-center" },
                React.createElement(StubText, null, "Select an item from the sidebar")));
        }
        if (selectedID === SYNTHETIC_ROW_IDS.DASHBOARD) {
            return React.createElement(DashboardPane, null);
        }
        if (selectedID === SYNTHETIC_ROW_IDS.QUICK_AGENTS) {
            return React.createElement(QuickAgentsPane, { rs: rs });
        }
        if (selectedID === SYNTHETIC_ROW_IDS.SETTINGS) {
            return React.createElement(SettingsPane, { rs: rs });
        }
        if (selectedID === SYNTHETIC_ROW_IDS.AUTOMATIONS) {
            return React.createElement(AutomationsPane, null);
        }
        if (selectedID === SYNTHETIC_ROW_IDS.HELP) {
            return React.createElement(HelpPane, null);
        }
        const taskDetails = props.data.taskDetails[selectedID];
        if (!taskDetails) {
            return (React.createElement("div", { className: "flex-1 flex items-center justify-center" },
                React.createElement(StubText, null, "Task not found")));
        }
        return React.createElement(TaskDetailPane, { rs: rs, taskID: selectedID, taskDetails: taskDetails });
    };
    const toolbarRhs = _.compact([
        {
            type: 'checkbox',
            width: 140,
            importance: 0,
            alwaysCollapse: true,
            lbl: 'Show Unapproved',
            value: rs.state.showUnapproved,
            onUpdate: (v) => {
                rs.updateState({ showUnapproved: v });
            },
        },
        {
            type: 'checkbox',
            width: 120,
            importance: 0,
            alwaysCollapse: true,
            lbl: 'Show Archived',
            value: rs.state.showArchived,
            onUpdate: (v) => {
                rs.updateState({ showArchived: v });
            },
        },
    ]);
    const openCreateTaskFlyout = React.useCallback(() => {
        openFlyoutForm({
            title: 'New Task',
            size: [520, 560],
            lblWidth: 80,
            formPrompt: 'Create a new task',
            defaultValue: () => ({
                title: '',
                note: '',
                allowAgents: false,
                attachments: [],
            }),
            fields: {
                title: FormType.Text({
                    lbl: 'Title',
                    maxLength: 80,
                    validators: [
                        {
                            req: (value) => value.trim().length > 0,
                            msg: 'is required',
                        },
                    ],
                }),
                note: FormType.Textarea({
                    lbl: 'Details',
                    rows: 6,
                    maxLength: TASK_NOTE_MAX_LENGTH,
                    placeholder: 'Describe the task...',
                }),
                allowAgents: FormType.Checkbox({
                    lbl: 'Allow Agent Automation',
                }),
                attachments: FormType.FileUploadMultiple({
                    lbl: 'Attachments',
                    height: 120,
                    fullWidth: true,
                    storageMode: 'v3',
                }),
            },
            onSave: (model, resolve) => {
                const uploadedItems = model.current.attachments.map(item => ({
                    guid: item.FileGuid ?? '',
                    filename: item.Filename,
                    mimeType: item.MimeType || 'application/octet-stream',
                }));
                if (uploadedItems.some(item => item.guid.length === 0)) {
                    resolve('Attachment missing GUID');
                    return;
                }
                props.taskCallbacks.onCreateTask({
                    title: model.current.title.trim(),
                    primaryNote: model.current.note,
                    allowAgents: model.current.allowAgents,
                    callbacks: {
                        onComplete: taskID => {
                            rs.updateState({ selectedRowID: taskID });
                            if (uploadedItems.length === 0) {
                                resolve(true);
                                return;
                            }
                            runPromisesSequentially(uploadedItems.map(item => () => new Promise((done, reject) => {
                                props.attachmentCallbacks.onAddAttachment({
                                    taskID,
                                    file: new File([], item.filename, {
                                        type: item.mimeType,
                                    }),
                                    fileGuid: item.guid,
                                    callbacks: {
                                        onComplete: () => {
                                            done();
                                        },
                                        onError: error => {
                                            reject(new Error(error));
                                        },
                                    },
                                });
                            })))
                                .then(() => {
                                resolve(true);
                            })
                                .catch(error => {
                                resolve(error instanceof Error
                                    ? `Failed to register attachments: ${error.message}`
                                    : `Failed to register attachments: ${String(error)}`);
                            });
                        },
                        onError: error => {
                            resolve(error);
                        },
                    },
                });
            },
        });
    }, [props.attachmentCallbacks, props.taskCallbacks, rs]);
    if (props.quickOnly) {
        return (React.createElement("div", { className: "tailwind-wrapper", style: { height: '100%' } },
            React.createElement(QuickOnlyPane, { rs: rs })));
    }
    return (React.createElement("div", { className: "tailwind-wrapper", style: { height: '100%' } },
        React.createElement(ResizeSplitter, { value: rs.state.mainSplitWidths, onUpdate: value => {
                rs.updateState({ mainSplitWidths: value });
            }, panes: [
                {
                    defaultWidth: 200,
                    minWidth: 180,
                    maxWidth: 600,
                    collapsible: false,
                    flexWidth: null,
                    content: (React.createElement("div", { className: "flex h-full flex-col" },
                        React.createElement(ToolbarNew, { className: "border-b border-neutral-300", lhs: [
                                {
                                    type: 'searchbox',
                                    width: 96,
                                    importance: 9,
                                    value: rs.state.sidebarSearchText,
                                    onUpdate: value => {
                                        rs.updateState({
                                            sidebarSearchText: value,
                                        });
                                    },
                                    placeholder: 'Search...',
                                },
                            ], rhs: [
                                {
                                    type: 'button-24',
                                    img: '/static/img/i8/w11-add.svg',
                                    title: 'Add task',
                                    width: 30,
                                    importance: 10,
                                    onClick: () => {
                                        openCreateTaskFlyout();
                                    },
                                },
                                ...toolbarRhs,
                            ] }),
                        React.createElement(List, { className: "min-h-0 flex-1", value: rs.state.selectedRowID, onUpdate: value => {
                                rs.updateState({ selectedRowID: value });
                            }, height: 30, multiple: false, rows: sidebarData.map(row => ({
                                value: getRowID(row),
                                height: 30,
                                className: row.type === 'task' && row.data.Status === 50
                                    ? 'text-neutral-400 hover:bg-yellow-100 cursor-pointer'
                                    : 'hover:bg-yellow-100 cursor-pointer',
                                content: () => (React.createElement(MainSidebarRow, { row: row, label: row.type === 'synthetic'
                                        ? row.label
                                        : (row.data.Title ??
                                            'Untitled Task') })),
                            })) }))),
                },
                {
                    defaultWidth: null,
                    minWidth: null,
                    maxWidth: null,
                    collapsible: false,
                    flexWidth: 1,
                    content: React.createElement(React.Fragment, null, renderMainContent()),
                },
            ] })));
};
const MainSidebarRow = (props) => (React.createElement("div", { className: "flex h-[30px] items-center gap-2 px-2" },
    props.row.type === 'synthetic' ? (React.createElement(AgentConsoleIcon, { name: props.row.icon, size: 18, className: "shrink-0" })) : (React.createElement("span", { className: "inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center" },
        React.createElement("span", { className: BuildClass([
                'h-2 w-2 rounded-full',
                getTaskStatusDotClass(props.row.data.Status),
            ]) }))),
    React.createElement("span", { className: "min-w-0 flex-1 truncate" }, props.label),
    props.row.type === 'task' && props.row.data.PendingChangesCount > 0 && (React.createElement("span", { className: "shrink-0 text-xs text-neutral-500" }, props.row.data.PendingChangesCount))));
const getTaskStatusDotClass = (status) => {
    if (status === 900) {
        return 'bg-green-500';
    }
    if (status >= 100 && status < 200) {
        return 'bg-blue-500';
    }
    if (status >= 200 && status < 900) {
        return 'bg-yellow-500';
    }
    if (status === 80 || (status >= 601 && status <= 603)) {
        return 'bg-red-500';
    }
    return 'bg-neutral-400';
};
const getTaskAttachmentIcon = (attachment) => {
    if (attachment.MimeType === 'application/pdf') {
        return 'pdf-file';
    }
    if (attachment.MimeType.startsWith('image/')) {
        return 'image-file';
    }
    return 'file';
};
const getAgentThreadIcon = (agentType) => {
    if (agentType.includes('categoriser')) {
        return 'thread-categoriser';
    }
    if (agentType.includes('worker')) {
        return 'thread-worker';
    }
    if (agentType.includes('collaboration')) {
        return 'thread-collaboration';
    }
    return 'thread';
};
const AgentConsoleIcon = (props) => {
    const common = {
        width: props.size,
        height: props.size,
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: 2,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        className: props.className,
        style: { width: props.size, height: props.size },
    };
    switch (props.name) {
        case 'dashboard':
            return (React.createElement("svg", { ...common },
                React.createElement("path", { d: "M4 11.5 12 5l8 6.5" }),
                React.createElement("path", { d: "M6.5 10.5V19h11v-8.5" }),
                React.createElement("path", { d: "M10 19v-5h4v5" })));
        case 'quick-agents':
            return (React.createElement("svg", { ...common },
                React.createElement("circle", { cx: "12", cy: "10", r: "3.2" }),
                React.createElement("path", { d: "M5.5 12v-1.6a6.5 6.5 0 0 1 13 0V12" }),
                React.createElement("path", { d: "M5.5 12.2v2.2a1.6 1.6 0 0 0 1.6 1.6h1.2v-5.4H7.1a1.6 1.6 0 0 0-1.6 1.6z" }),
                React.createElement("path", { d: "M18.5 12.2v2.2a1.6 1.6 0 0 1-1.6 1.6h-1.2v-5.4h1.2a1.6 1.6 0 0 1 1.6 1.6z" }),
                React.createElement("path", { d: "M9.5 19a4.8 4.8 0 0 1 5 0" })));
        case 'automations':
            return (React.createElement("svg", { ...common },
                React.createElement("circle", { cx: "12", cy: "12", r: "7.5" }),
                React.createElement("path", { d: "M12 8v4.5l3 1.8" }),
                React.createElement("path", { d: "M5.5 5.5 4 4" }),
                React.createElement("path", { d: "M18.5 5.5 20 4" })));
        case 'settings':
            return (React.createElement("svg", { ...common },
                React.createElement("circle", { cx: "12", cy: "12", r: "2.6" }),
                React.createElement("path", { d: "M12 3.8v2" }),
                React.createElement("path", { d: "M12 18.2v2" }),
                React.createElement("path", { d: "m6.2 6.2 1.4 1.4" }),
                React.createElement("path", { d: "m16.4 16.4 1.4 1.4" }),
                React.createElement("path", { d: "M3.8 12h2" }),
                React.createElement("path", { d: "M18.2 12h2" }),
                React.createElement("path", { d: "m6.2 17.8 1.4-1.4" }),
                React.createElement("path", { d: "m16.4 7.6 1.4-1.4" })));
        case 'help':
            return (React.createElement("svg", { ...common },
                React.createElement("circle", { cx: "12", cy: "12", r: "8" }),
                React.createElement("path", { d: "M9.8 9.4a2.4 2.4 0 1 1 3.7 2c-.9.6-1.5 1.1-1.5 2.1" }),
                React.createElement("path", { d: "M12 17h.01" })));
        case 'overview':
            return (React.createElement("svg", { ...common },
                React.createElement("rect", { x: "4", y: "5", width: "6", height: "5", rx: "1" }),
                React.createElement("rect", { x: "14", y: "5", width: "6", height: "5", rx: "1" }),
                React.createElement("rect", { x: "4", y: "14", width: "6", height: "5", rx: "1" }),
                React.createElement("path", { d: "M14 15h6" }),
                React.createElement("path", { d: "M14 18h4" })));
        case 'review':
            return (React.createElement("svg", { ...common },
                React.createElement("path", { d: "M5 6h9" }),
                React.createElement("path", { d: "M5 12h7" }),
                React.createElement("path", { d: "M5 18h5" }),
                React.createElement("path", { d: "m15 16 2 2 4-5" })));
        case 'task-details':
            return (React.createElement("svg", { ...common },
                React.createElement("path", { d: "M7 4.5h7l3 3V19.5H7z" }),
                React.createElement("path", { d: "M14 4.5v3h3" }),
                React.createElement("path", { d: "M9.5 11h5" }),
                React.createElement("path", { d: "M9.5 14h5" }),
                React.createElement("path", { d: "M9.5 17h3" })));
        case 'scratchpad':
            return (React.createElement("svg", { ...common },
                React.createElement("path", { d: "M7 5h10v14H7z" }),
                React.createElement("path", { d: "M9 8h6" }),
                React.createElement("path", { d: "M9 11h6" }),
                React.createElement("path", { d: "M9 14h3" }),
                React.createElement("path", { d: "m14.5 17 3-3" })));
        case 'discussion':
            return (React.createElement("svg", { ...common },
                React.createElement("path", { d: "M5 6.5h14v9H9l-4 3z" }),
                React.createElement("path", { d: "M8 10h8" }),
                React.createElement("path", { d: "M8 13h5" })));
        case 'attachments':
            return (React.createElement("svg", { ...common },
                React.createElement("path", { d: "m8 12.5 5.5-5.5a3 3 0 0 1 4.2 4.2l-7 7a4.2 4.2 0 0 1-6-6l6.7-6.7" }),
                React.createElement("path", { d: "m9.5 14 5.5-5.5" })));
        case 'pdf-file':
            return (React.createElement("svg", { ...common },
                React.createElement("path", { d: "M7 4.5h7l3 3V19.5H7z" }),
                React.createElement("path", { d: "M14 4.5v3h3" }),
                React.createElement("path", { d: "M8.8 15.5v-4h1.4a1.2 1.2 0 0 1 0 2.4H8.8" }),
                React.createElement("path", { d: "M12.2 15.5v-4h1a2 2 0 0 1 0 4z" }),
                React.createElement("path", { d: "M15.7 15.5v-4h2" }),
                React.createElement("path", { d: "M15.7 13.3h1.5" })));
        case 'image-file':
            return (React.createElement("svg", { ...common },
                React.createElement("rect", { x: "5", y: "5", width: "14", height: "14", rx: "1.5" }),
                React.createElement("circle", { cx: "9", cy: "9", r: "1.2" }),
                React.createElement("path", { d: "m6.5 17 4.5-4.5 2.8 2.8 1.7-1.7 2 3.4" })));
        case 'file':
            return (React.createElement("svg", { ...common },
                React.createElement("path", { d: "M7 4.5h7l3 3V19.5H7z" }),
                React.createElement("path", { d: "M14 4.5v3h3" }),
                React.createElement("path", { d: "M9.5 12h5" }),
                React.createElement("path", { d: "M9.5 15h5" })));
        case 'thread-categoriser':
            return (React.createElement("svg", { ...common },
                React.createElement("circle", { cx: "6.5", cy: "7", r: "2" }),
                React.createElement("circle", { cx: "17.5", cy: "7", r: "2" }),
                React.createElement("circle", { cx: "12", cy: "17", r: "2" }),
                React.createElement("path", { d: "M8.2 8.3 11 15" }),
                React.createElement("path", { d: "m15.8 8.3-2.8 6.7" })));
        case 'thread-worker':
            return (React.createElement("svg", { ...common },
                React.createElement("rect", { x: "6", y: "8", width: "12", height: "9", rx: "2" }),
                React.createElement("path", { d: "M9 8V6h6v2" }),
                React.createElement("path", { d: "M9.5 12h.01" }),
                React.createElement("path", { d: "M14.5 12h.01" }),
                React.createElement("path", { d: "M10 15h4" })));
        case 'thread-collaboration':
            return (React.createElement("svg", { ...common },
                React.createElement("circle", { cx: "8", cy: "9", r: "2.5" }),
                React.createElement("circle", { cx: "16", cy: "9", r: "2.5" }),
                React.createElement("path", { d: "M4.5 18a3.6 3.6 0 0 1 7 0" }),
                React.createElement("path", { d: "M12.5 18a3.6 3.6 0 0 1 7 0" })));
        case 'thread':
            return (React.createElement("svg", { ...common },
                React.createElement("path", { d: "M5 7h14v8H9l-4 3z" }),
                React.createElement("path", { d: "M8 10h8" }),
                React.createElement("path", { d: "M8 13h5" })));
        default: {
            const exhaustive = props.name;
            return exhaustive;
        }
    }
};
const QuickOnlyPane = ({ rs }) => {
    const tabs = [
        {
            value: 'chat',
            label: 'Chat',
            content: () => React.createElement(QuickAgentsPane, { rs: rs }),
        },
        {
            value: 'skills',
            label: 'Skills',
            content: () => React.createElement(SkillsSettingsTab, { rs: rs }),
        },
        {
            value: 'commands',
            label: 'Commands',
            content: () => rs.props.quickCommandsPane?.() ?? React.createElement(CommandsPlaceholderPane, null),
        },
    ];
    return (React.createElement("div", { className: "h-full min-h-0 flex flex-col" },
        React.createElement(TabControl, { tabs: tabs, value: rs.state.currentQuickOnlyTab, onUpdate: value => {
                rs.updateState({ currentQuickOnlyTab: value });
            }, className: "flex-1 min-h-0" })));
};
const CommandsPlaceholderPane = () => (React.createElement("div", { className: "h-full flex items-center justify-center p-6 text-neutral-600" },
    React.createElement("div", { className: "max-w-md rounded-lg border border-dashed border-neutral-300 bg-neutral-50 p-5 text-center shadow-sm" },
        React.createElement("img", { src: "/static/img/i8/ios16-todo-list.svg", alt: "", className: "mx-auto mb-3 h-10 w-10 opacity-60" }),
        React.createElement("h2", { className: "mb-2 text-base font-medium text-neutral-800" }, "No commands available"),
        React.createElement("p", { className: "text-sm leading-5" }, "This view does not have command shortcuts configured yet."))));
const DashboardPane = () => (React.createElement("div", { className: "h-full flex flex-col items-center justify-center p-8 text-neutral-500" },
    React.createElement(AgentConsoleIcon, { name: "dashboard", size: 64, className: "mb-4 opacity-50" }),
    React.createElement("h2", { className: "text-lg font-medium mb-2" }, "Dashboard"),
    React.createElement("p", { className: "text-sm text-center max-w-md" }, "Analytics and queue insights will be displayed here in a future update.")));
const AutomationsPane = () => (React.createElement("div", { className: "h-full flex flex-col items-center justify-center p-8 text-neutral-500" },
    React.createElement(AgentConsoleIcon, { name: "automations", size: 64, className: "mb-4 opacity-50" }),
    React.createElement("h2", { className: "text-lg font-medium mb-2" }, "Automations"),
    React.createElement("p", { className: "text-sm text-center max-w-md" }, "Automations will include task templates and other repeatable task workflows.")));
const HelpPane = () => (React.createElement("div", { className: "h-full flex flex-col items-center justify-center p-8 text-neutral-500" },
    React.createElement(AgentConsoleIcon, { name: "help", size: 64, className: "mb-4 opacity-50" }),
    React.createElement("h2", { className: "text-lg font-medium mb-2" }, "Help"),
    React.createElement("p", { className: "text-sm text-center max-w-md" }, "Task harness guidance will be displayed here in a future update.")));
const QuickAgentsPane = ({ rs }) => {
    const props = rs.props;
    const quickAgentThreads = _.chain(props.data.agentThreads)
        .pickBy(thread => !thread.metadata.parentTask && !thread.metadata.parentThread)
        .value();
    return (React.createElement("div", { className: "h-full" },
        React.createElement(AgentUI, { threads: quickAgentThreads, currentUserID: props.currentUserID, facility: props.facility, subFacility: props.subFacility ?? 0, agentType: props.applicationType, changeRenderers: props.changeRenderers, widgetToGroupCode: props.widgetToGroupCode, toolRenderers: props.toolRenderers, toolHandlers: props.toolHandlers, theme: props.theme, hue: props.hue, onSendMessage: input => {
                props.agentThreadCallbacks.onSendMessage({
                    threadID: input.threadID,
                    message: input.message,
                    persistMessage: input.persistMessage,
                    pendingChanges: input.pendingChanges,
                    pendingQuestions: input.pendingQuestions,
                    intelligence: input.intelligence,
                    callbacks: input.callbacks,
                });
            }, onApproveChanges: input => {
                props.agentThreadCallbacks.onApproveChanges({
                    threadIDs: input.threadIDs,
                    approvalIDs: input.approvalIDs,
                    callbacks: input.callbacks,
                });
            }, onRejectChanges: input => {
                props.agentThreadCallbacks.onRejectChanges({
                    approvalIDs: input.approvalIDs,
                    callbacks: input.callbacks,
                });
            }, onLoadThreadContent: props.agentThreadCallbacks.onLoadThreadContent
                ? input => {
                    props.agentThreadCallbacks.onLoadThreadContent?.(input);
                }
                : undefined, onSubmitQuestions: props.agentThreadCallbacks.onSubmitQuestions
                ? input => {
                    props.agentThreadCallbacks.onSubmitQuestions?.(input);
                }
                : undefined, onThreadAction: props.agentThreadCallbacks.onThreadAction
                ? input => {
                    props.agentThreadCallbacks.onThreadAction?.({
                        threadIDs: input.threadIDs,
                        action: input.action,
                        callbacks: input.callbacks,
                    });
                }
                : undefined, onDeleteMessage: rs.props.agentThreadCallbacks.onDeleteMessage
                ? input => {
                    rs.props.agentThreadCallbacks.onDeleteMessage?.(input);
                }
                : undefined })));
};
const SettingsPane = ({ rs }) => {
    const tabs = [
        {
            value: 'email',
            label: 'Email',
            content: () => React.createElement(EmailSettingsTab, { rs: rs }),
        },
        {
            value: 'skills',
            label: 'Skills',
            content: () => React.createElement(SkillsSettingsTab, { rs: rs }),
        },
        {
            value: 'instructions',
            label: 'Instructions',
            content: () => React.createElement(InstructionSettingsTab, { rs: rs }),
        },
    ];
    return (React.createElement("div", { className: "h-full flex flex-col" },
        React.createElement(TabControl, { tabs: tabs, value: rs.state.currentSettingsTab, onUpdate: v => {
                rs.updateState({ currentSettingsTab: v });
            }, className: "flex-1" })));
};
const INSTRUCTION_SETTINGS = [
    {
        id: 'user',
        title: 'User Preferences',
        description: 'Short personal preferences for how agents should communicate and prioritise work.',
        placeholder: 'Example: Prefer concise answers, highlight assumptions, and only ask questions when blocked.',
        maxLength: 600,
    },
    {
        id: 'categoriser-general',
        title: 'Categoriser',
        description: 'General instructions for interpreting task details and agent scratchpad context.',
        placeholder: 'Example: Route tasks according to their content unless the task details say otherwise.',
        maxLength: 4000,
    },
    {
        id: 'categoriser-attachments',
        title: 'Attachment Naming',
        description: 'Rules for renaming or titling attachments after the categoriser reads them.',
        placeholder: 'Example: Use "<source> <document type> <reference number>" for attachments.',
        maxLength: 4000,
    },
    {
        id: 'categoriser-summaries',
        title: 'Attachment Summaries',
        description: 'Style and detail expectations for attachment summaries written back to the task.',
        placeholder: 'Example: Include people, reference numbers, dates, amounts, and any handling notes.',
        maxLength: 4000,
    },
    {
        id: 'categoriser-relevance',
        title: 'Attachment Relevance',
        description: 'Facility-specific guidance for when attachments should be kept, disabled, or ignored.',
        placeholder: 'Example: Disable email signature images, blank pages, and virus scan disclaimers.',
        maxLength: 4000,
    },
];
const InstructionSettingsTab = ({ rs }) => (React.createElement("div", { className: "h-full flex flex-col" },
    React.createElement("div", { className: "border-b border-neutral-200 px-4 py-3" },
        React.createElement("h2", { className: "text-lg font-medium" }, "Instructions"),
        React.createElement("p", { className: "text-sm text-neutral-500 mt-1" }, "Configure targeted prompting for task agents and categorisation.")),
    React.createElement("div", { className: "flex-1 overflow-y-auto p-4 space-y-4" }, INSTRUCTION_SETTINGS.map(setting => (React.createElement("section", { key: setting.id, className: "border border-neutral-200 rounded bg-white p-3" },
        React.createElement("div", { className: "mb-2" },
            React.createElement("h3", { className: "text-sm font-medium" }, setting.title),
            React.createElement("p", { className: "text-xs text-neutral-500 mt-1" }, setting.description)),
        React.createElement(TextareaWithVoice, { value: rs.state.instructionDrafts[setting.id], onUpdate: value => {
                rs.updateState(state => ({
                    instructionDrafts: {
                        ...state.instructionDrafts,
                        [setting.id]: value,
                    },
                }));
            }, prompt: setting.description, maxLength: setting.maxLength, placeholder: setting.placeholder, className: "w-full h-[120px]", classNameTextarea: "text-sm leading-5" })))))));
const EmailSettingsTab = ({ rs }) => {
    const props = rs.props;
    const [selectedReceiverID, setSelectedReceiverID] = React.useState(null);
    const [isCreating, setIsCreating] = React.useState(false);
    const previewHex = React.useMemo(() => guid().slice(0, 8), []);
    const receivers = props.data.emailReceivers;
    const selectedAllowlist = selectedReceiverID
        ? (props.data.emailAllowlist[selectedReceiverID] ?? [])
        : [];
    const selectedReceiver = selectedReceiverID
        ? (receivers.find(receiver => receiver.ID === selectedReceiverID) ?? null)
        : null;
    const generateEmailPreview = (friendlyName) => {
        if (!friendlyName) {
            return `<name>-${props.appName}-<hex>@trionline.com.au`;
        }
        return `${friendlyName}-${props.appName}-${previewHex}@trionline.com.au`;
    };
    const isValidFriendlyName = (name) => name.length > 0 && name.length <= 8 && /^[a-zA-Z0-9]+$/.test(name);
    const handleCreateReceiver = () => {
        const name = rs.state.newReceiverName.trim().toLowerCase();
        if (!isValidFriendlyName(name)) {
            return;
        }
        setIsCreating(true);
        props.emailSettingsCallbacks.onCreateReceiver({
            friendlyName: name,
            callbacks: {
                onComplete: receiver => {
                    rs.updateState({ newReceiverName: '' });
                    setSelectedReceiverID(receiver.ID);
                    setIsCreating(false);
                },
                onError: err => {
                    console.error('Failed to create receiver:', err);
                    setIsCreating(false);
                },
            },
        });
    };
    const allowlistFields = [
        GridCell.Text({
            key: 'Name',
            lbl: 'Name',
            value: x => x.Name ?? '',
            onUpdate: (v, r) => {
                r.Name = v;
            },
        }),
        GridCell.Text({
            key: 'SenderAddress',
            lbl: 'Sender Address',
            value: x => x.SenderAddress,
            onUpdate: (v, r) => {
                r.SenderAddress = v;
            },
        }),
    ];
    const friendlyNameTrimmed = rs.state.newReceiverName.trim().toLowerCase();
    const isValid = isValidFriendlyName(friendlyNameTrimmed);
    const hasInput = rs.state.newReceiverName.length > 0;
    return (React.createElement("div", { className: "h-full flex flex-col" },
        React.createElement("div", { className: "border-b border-neutral-200 px-4 py-3" },
            React.createElement("h2", { className: "text-lg font-medium" }, "Email Inbox Settings"),
            React.createElement("p", { className: "text-sm text-neutral-500 mt-1" }, "Manage email receivers and sender allowlists for task creation.")),
        React.createElement("div", { className: "flex-1 flex overflow-hidden" },
            React.createElement("div", { className: "w-80 border-r border-neutral-200 flex flex-col" },
                React.createElement("div", { className: "p-3 border-b border-neutral-100" },
                    React.createElement("h3", { className: "text-sm font-medium mb-2" }, "Add Email Receiver"),
                    React.createElement("p", { className: "text-xs text-neutral-500 mb-2" },
                        "Format:",
                        ' ',
                        React.createElement("code", { className: "bg-neutral-100 px-1 rounded" }, generateEmailPreview(''))),
                    React.createElement("div", { className: "flex gap-2" },
                        React.createElement(Textbox, { className: "flex-1", placeholder: "Friendly name", value: rs.state.newReceiverName, onUpdate: v => {
                                rs.updateState({
                                    newReceiverName: v.replace(/[^a-zA-Z0-9]/g, ''),
                                });
                            }, maxLength: 8 }),
                        React.createElement(Button, { type: "submit", disabled: isCreating || !isValid, onClick: handleCreateReceiver }, "Add")),
                    hasInput && (React.createElement("div", { className: BuildClass({
                            'text-xs mt-1': true,
                            'text-neutral-500': isValid,
                            'text-red-500': !isValid,
                        }) }, isValid
                        ? `Will create: ${generateEmailPreview(friendlyNameTrimmed)}`
                        : 'Name must be 1-8 alphanumeric characters'))),
                React.createElement("div", { className: "p-2 border-b border-neutral-100 bg-neutral-50" },
                    React.createElement("h4", { className: "text-xs font-medium text-neutral-600" }, "Configured Receivers")),
                React.createElement("div", { className: "flex-1 overflow-y-auto" }, receivers.length === 0 ? (React.createElement("div", { className: "p-4 text-sm text-neutral-500 text-center" }, "No email receivers configured")) : (React.createElement("div", { className: "divide-y divide-neutral-100" }, receivers.map(receiver => (React.createElement("div", { key: receiver.ID, className: BuildClass({
                        'p-3 cursor-pointer hover:bg-neutral-50': true,
                        'bg-blue-50': selectedReceiverID === receiver.ID,
                    }), onClick: () => {
                        setSelectedReceiverID(receiver.ID);
                    } },
                    React.createElement("div", { className: "flex items-center gap-2" },
                        React.createElement(Checkbox, { lbl: "", value: !receiver.IsDisabled, onUpdate: () => {
                                props.emailSettingsCallbacks.onToggleReceiver({
                                    receiverID: receiver.ID,
                                    isDisabled: !receiver.IsDisabled,
                                    callbacks: {
                                        onComplete: () => { },
                                        onError: err => {
                                            console.error(err);
                                        },
                                    },
                                });
                            }, title: receiver.IsDisabled
                                ? 'Enable receiver'
                                : 'Disable receiver' }),
                        React.createElement("div", { className: "flex-1 min-w-0" },
                            React.createElement("div", { className: BuildClass({
                                    'text-sm font-medium truncate': true,
                                    'text-neutral-400': receiver.IsDisabled,
                                }) }, receiver.Description ??
                                receiver.EmailAddress.split('@')[0]),
                            React.createElement("div", { className: "text-xs text-neutral-500 truncate" }, receiver.EmailAddress)))))))))),
            React.createElement("div", { className: "flex-1 flex flex-col" }, selectedReceiver ? (React.createElement(React.Fragment, null,
                React.createElement("div", { className: "border-b border-neutral-100 px-4 py-3" },
                    React.createElement("h3", { className: "text-sm font-medium" }, "Sender Allowlist"),
                    React.createElement("p", { className: "text-xs text-neutral-500 mt-1" },
                        "Allowed senders for",
                        ' ',
                        React.createElement("span", { className: "font-medium" }, selectedReceiver.EmailAddress))),
                React.createElement("div", { className: "flex-1 p-4 overflow-y-auto" },
                    React.createElement(EditableGrid, { className: "w-full", data: selectedAllowlist, pk: entry => entry.ID, defaultSortingKey: "SenderAddress", deletable: true, appendable: () => ({
                            ID: null,
                            EmailAddressID: selectedReceiver.ID,
                            SenderAddress: '',
                            Name: '',
                        }), fields: allowlistFields, onSave: (delta, resolve) => {
                            const appendedPromises = _.chain(delta.appended)
                                .map(async (entry) => new Promise((res, rej) => {
                                props.emailSettingsCallbacks.onAddAllowlistEntry({
                                    receiverID: selectedReceiver.ID,
                                    name: entry.Name ?? '',
                                    senderAddress: entry.SenderAddress ??
                                        '',
                                    callbacks: {
                                        onComplete: () => {
                                            res();
                                        },
                                        onError: err => {
                                            rej(new Error(err));
                                        },
                                    },
                                });
                            }))
                                .value();
                            const updatedPromises = _.chain(delta.updated)
                                .map(async (updates, key) => {
                                const existing = selectedAllowlist.find(entry => entry.ID === Number(key));
                                if (!existing) {
                                    return null;
                                }
                                const name = updates.Name ?? existing.Name ?? '';
                                const senderAddress = updates.SenderAddress ??
                                    existing.SenderAddress;
                                return new Promise((res, rej) => {
                                    props.emailSettingsCallbacks.onDeleteAllowlistEntry({
                                        entryID: existing.ID,
                                        callbacks: {
                                            onComplete: () => {
                                                props.emailSettingsCallbacks.onAddAllowlistEntry({
                                                    receiverID: selectedReceiver.ID,
                                                    name,
                                                    senderAddress,
                                                    callbacks: {
                                                        onComplete: () => {
                                                            res();
                                                        },
                                                        onError: err => {
                                                            rej(new Error(err));
                                                        },
                                                    },
                                                });
                                            },
                                            onError: err => {
                                                rej(new Error(err));
                                            },
                                        },
                                    });
                                });
                            })
                                .compact()
                                .value();
                            const deletedPromises = _.chain(delta.deleted)
                                .map(async (id) => new Promise((res, rej) => {
                                props.emailSettingsCallbacks.onDeleteAllowlistEntry({
                                    entryID: id,
                                    callbacks: {
                                        onComplete: () => {
                                            res();
                                        },
                                        onError: err => {
                                            rej(new Error(err));
                                        },
                                    },
                                });
                            }))
                                .value();
                            const promises = _.concat(appendedPromises, updatedPromises, deletedPromises);
                            if (promises.length === 0) {
                                resolve(true);
                                return;
                            }
                            Promise.all(promises)
                                .then(() => {
                                resolve(true);
                            })
                                .catch(err => {
                                resolve(String(err));
                            });
                        } })))) : (React.createElement("div", { className: "flex-1 flex items-center justify-center text-neutral-500" }, "Select a receiver to manage its allowlist"))))));
};
const SkillsSettingsTab = ({ rs }) => {
    const props = rs.props;
    const [selectedSkillID, setSelectedSkillID] = React.useState(null);
    const [skillsFormMode, setSkillsFormMode] = React.useState('read');
    const [skillsSblgView, setSkillsSblgView] = React.useState({
        listGridView: {
            hiddenFields: ['updated'],
        },
    });
    const [skillsFormMessage, setSkillsFormMessage] = React.useState('');
    const [skillsFormLoading, setSkillsFormLoading] = React.useState(null);
    const pendingCreatedSkillID = React.useRef(null);
    const fullSkillTextareaRef = React.useRef(null);
    const skillsEditFormRef = React.useRef(null);
    const scopedSubFacility = props.skillsUseSubFacilityScope
        ? (props.subFacility ?? null)
        : null;
    const isInternalUser = rs.props.currentUserID < 100000;
    const isSkillGlobalOverrideUser = [24222, 49067].includes(rs.props.currentUserID);
    const scopedSkills = _.filter(props.data.skills, skill => skill.Facility === props.facility &&
        (skill.SubFacility ?? null) === scopedSubFacility &&
        skill.ApplicationType === props.applicationType);
    const globalSkills = _.filter(props.data.skills, skill => (skill.Facility === 0 || skill.Facility == null) &&
        (skill.SubFacility ?? null) === null &&
        skill.ApplicationType === props.applicationType);
    const visibleSkills = _.sortBy(_.uniqBy([...scopedSkills, ...(isInternalUser ? globalSkills : [])], skill => skill.ID), skill => skill.Title.toLocaleLowerCase());
    const selectedSkill = selectedSkillID == null
        ? null
        : (visibleSkills.find(skill => String(skill.ID) === selectedSkillID) ?? null);
    const isGlobalSelected = (selectedSkill?.Facility === 0 || selectedSkill?.Facility == null) &&
        (selectedSkill?.SubFacility ?? null) === null;
    const isGlobalReadOnly = isGlobalSelected && !isSkillGlobalOverrideUser;
    React.useEffect(() => {
        const hasSelectedSkill = selectedSkillID != null &&
            visibleSkills.some(skill => String(skill.ID) === selectedSkillID);
        if (hasSelectedSkill) {
            if (pendingCreatedSkillID.current === selectedSkillID) {
                pendingCreatedSkillID.current = null;
            }
            return;
        }
        if (selectedSkillID != null &&
            pendingCreatedSkillID.current === selectedSkillID) {
            return;
        }
        const fallbackSkillID = _.head(visibleSkills)
            ? String(_.head(visibleSkills)?.ID)
            : null;
        if (fallbackSkillID !== selectedSkillID) {
            setSelectedSkillID(fallbackSkillID);
        }
    }, [selectedSkillID, visibleSkills]);
    const selectedSkillFormValue = React.useMemo(() => ({
        title: selectedSkill?.Title ?? '',
        description: selectedSkill?.Description ?? '',
        document: selectedSkill?.Document ?? '',
    }), [
        selectedSkill?.Description,
        selectedSkill?.Document,
        selectedSkill?.ID,
        selectedSkill?.Title,
    ]);
    const [skillFormValue, setSkillFormValue] = React.useState(selectedSkillFormValue);
    React.useEffect(() => {
        if (skillsFormMode === 'edit') {
            return;
        }
        setSkillFormValue(selectedSkillFormValue);
    }, [selectedSkillFormValue, skillsFormMode]);
    React.useEffect(() => {
        setSkillsFormMode('read');
        setSkillsFormMessage('');
        setSkillsFormLoading(null);
    }, [selectedSkillID]);
    const sidebarFields = [
        {
            key: 'title',
            lbl: 'Skill',
            text: skill => skill.Title,
            className: () => TW_OVERFLOW_ELLIPSIS,
            sortVal: skill => skill.Title.toLocaleLowerCase(),
            groupVal: skill => skill.Facility === 0 || skill.Facility == null ? 'global' : 'scoped',
            groupName: groupID => (groupID === 'global' ? 'Global' : 'Scoped'),
            groupSort: group => (group.ID === 'global' ? 0 : 1),
        },
        {
            key: 'updated',
            lbl: 'Updated',
            width: { init: 120 },
            text: skill => DateTimeObj.parse(skill.UpdatedAt)?.fmt('d LLL yyyy') ?? skill.UpdatedAt,
            className: () => 'text-xs text-neutral-500',
            sortVal: skill => skill.UpdatedAt,
        },
    ];
    const skillFormFields = {
        title: FormType.Text({
            lbl: 'Title',
            maxLength: 40,
            validators: [
                {
                    req: (value) => value.trim().length > 0,
                    msg: 'is required',
                },
            ],
        }),
        description: FormType.Textarea({
            lbl: 'Description',
            rows: 2,
            maxLength: 160,
            disableNewlines: true,
        }),
        document: (() => {
            const field = FormType.Textarea({
                lbl: 'Full Skill',
                fullWidth: true,
                rows: 8,
                maxLength: 10000,
                className: 'flex-1 min-h-0 [&_.cmpt]:h-full [&_.cmpt]:min-h-0 [&_.cmpt]:flex [&_.cmpt]:flex-col',
                validators: [
                    {
                        req: (value) => value.trim().length > 0,
                        msg: 'is required',
                    },
                ],
            });
            return {
                ...field,
                jsx: (...args) => {
                    const [input] = args;
                    const hasTextareaClassName = (value) => typeof value === 'object' &&
                        value != null &&
                        'classNameTextarea' in value;
                    const existingTextareaClassName = hasTextareaClassName(input)
                        ? input.classNameTextarea
                        : undefined;
                    return (React.createElement(TextareaWithVoice, { value: input.value, onUpdate: input.onUpdate, className: BuildClass([
                            'ui5 ui5-textarea tailwind-wrapper w-full align-top h-full js-skill-full-surface font-mono',
                            input.className,
                        ]), classNameTextarea: BuildClass([
                            'font-mono text-[13px] leading-[18px]',
                            existingTextareaClassName,
                        ]), style: {
                            fontFamily: 'Cascadia Code, monospace',
                        }, title: input.title ?? undefined, rows: 8, readOnly: input.readOnly, disabled: input.disabled, onFocus: input.onFocus, onBlur: input.onBlur, "data-1p-ignore": true, prompt: '', maxLength: 10000 }));
                },
            };
        })(),
    };
    const normaliseSkillDescription = (description) => description.replace(/\r?\n/g, ' ');
    const estimatedTokenCount = React.useMemo(() => {
        const composedText = [
            skillFormValue.title ?? '',
            skillFormValue.description ?? '',
            skillFormValue.document ?? '',
        ].join('\n');
        return Math.ceil(composedText.length / 4);
    }, [skillFormValue.description, skillFormValue.document, skillFormValue.title]);
    const SKILL_TOKEN_CAP = 2000;
    const skillTokenTiers = React.useMemo(() => [
        { upTo: 1400, colour: 'oklch(0.62 0 0)' },
        { upTo: 1800, colour: 'oklch(0.72 0.16 65)' },
        { upTo: Number.POSITIVE_INFINITY, colour: 'oklch(0.62 0.23 28)' },
    ], []);
    const saveSkillFromToolbar = React.useCallback(() => {
        if (!selectedSkill || isGlobalReadOnly) {
            setSkillsFormMessage('Global skills are read-only');
            return;
        }
        const title = skillFormValue.title ?? '';
        const description = skillFormValue.description ?? '';
        const document = skillFormValue.document ?? '';
        const validationErrors = _.compact([
            title.trim().length === 0 ? 'Invalid Title: is required' : null,
            title.length > 40 ? 'Invalid Title: must be 40 characters or less' : null,
            description.length > 160
                ? 'Invalid Description: must be 160 characters or less'
                : null,
            document.trim().length === 0 ? 'Invalid Full Skill: is required' : null,
            document.length > 10000
                ? 'Invalid Full Skill: must be 10000 characters or less'
                : null,
        ]);
        if (validationErrors.length > 0) {
            setSkillsFormMessage(validationErrors.join('\n'));
            return;
        }
        setSkillsFormLoading('save');
        setSkillsFormMessage('');
        rs.props.skillsSettingsCallbacks.onUpdateSkill({
            skillID: selectedSkill.ID,
            applicationType: rs.props.applicationType,
            title,
            description: normaliseSkillDescription(description),
            document,
            callbacks: {
                onComplete: () => {
                    setSkillsFormLoading(null);
                    setSkillsFormMode('read');
                },
                onError: err => {
                    setSkillsFormLoading(null);
                    setSkillsFormMessage(err);
                },
            },
        });
    }, [
        isGlobalReadOnly,
        normaliseSkillDescription,
        rs.props.applicationType,
        rs.props.skillsSettingsCallbacks,
        selectedSkill,
        skillFormValue.description,
        skillFormValue.document,
        skillFormValue.title,
    ]);
    const cancelSkillEditFromToolbar = React.useCallback(() => {
        setSkillFormValue(selectedSkillFormValue);
        setSkillsFormMessage('');
        setSkillsFormMode('read');
    }, [selectedSkillFormValue]);
    const deleteSkillFromToolbar = React.useCallback(() => {
        if (!selectedSkill || isGlobalReadOnly) {
            setSkillsFormMessage('Global skills are read-only');
            return;
        }
        PromptConfirm({
            title: 'Delete skill?',
            msg: 'This will permanently delete this skill. This action cannot be undone.',
            size: [320, 190],
            yes: done => {
                setSkillsFormLoading('delete');
                rs.props.skillsSettingsCallbacks.onDeleteSkill({
                    skillID: selectedSkill.ID,
                    applicationType: rs.props.applicationType,
                    callbacks: {
                        onComplete: () => {
                            setSelectedSkillID(null);
                            setSkillsFormMode('read');
                            setSkillsFormLoading(null);
                            setSkillsFormMessage('');
                            done(true);
                        },
                        onError: err => {
                            setSkillsFormLoading(null);
                            setSkillsFormMessage(err);
                            done(true);
                        },
                    },
                });
            },
        });
    }, [
        isGlobalReadOnly,
        rs.props.applicationType,
        rs.props.skillsSettingsCallbacks,
        selectedSkill,
    ]);
    const openCreateSkillFlyout = React.useCallback(() => {
        openFlyoutForm({
            title: 'New Skill',
            size: [640, 760],
            lblWidth: 100,
            formPrompt: 'Create a scoped skill for this workspace',
            defaultValue: () => ({
                title: '',
                description: '',
                document: '',
            }),
            fields: skillFormFields,
            onSave: (model, resolve) => {
                props.skillsSettingsCallbacks.onCreateSkill({
                    facility: props.facility,
                    subFacility: scopedSubFacility,
                    applicationType: props.applicationType,
                    title: model.current.title,
                    description: normaliseSkillDescription(model.current.description),
                    document: model.current.document,
                    callbacks: {
                        onComplete: skill => {
                            const nextSkillID = String(skill.ID);
                            pendingCreatedSkillID.current = nextSkillID;
                            setSelectedSkillID(nextSkillID);
                            resolve(true);
                        },
                        onError: err => {
                            resolve(err);
                        },
                    },
                });
            },
        });
    }, [
        normaliseSkillDescription,
        props.applicationType,
        props.facility,
        props.skillsSettingsCallbacks,
        scopedSubFacility,
        skillFormFields,
    ]);
    const openSkillsToolbarMenu = React.useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        const menuItems = [
            {
                label: 'Delete',
                icon: '/static/img/i8/color-trash-delete.svg',
                disabled: skillsFormLoading != null ||
                    !selectedSkill ||
                    Boolean(isGlobalReadOnly),
                onClick: () => {
                    deleteSkillFromToolbar();
                },
            },
        ];
        setContextMenu({
            position: {
                x: rect.left,
                y: rect.bottom,
            },
            items: menuItems,
            width: 180,
        });
    }, [deleteSkillFromToolbar, isGlobalReadOnly, selectedSkill, skillsFormLoading]);
    const composedSkillMarkdown = React.useMemo(() => _.compact([
        skillFormValue.title.trim().length > 0
            ? `# ${skillFormValue.title}`
            : null,
        skillFormValue.description.trim().length > 0
            ? `> ${skillFormValue.description}`
            : null,
        skillFormValue.document,
    ]).join('\n\n'), [skillFormValue.description, skillFormValue.document, skillFormValue.title]);
    const markdownWrapperStyle = React.useMemo(() => getChatCSSVars(undefined, (rs.props.theme ?? 'light') === 'dark'), [rs.props.theme]);
    const showSystemSkillReadNotice = skillsFormMode === 'read' && isInternalUser && Boolean(isGlobalSelected);
    return (React.createElement("div", { className: "h-full min-h-0 flex flex-col" },
        React.createElement(SidebarListGrid, { pk: skill => String(skill.ID), data: visibleSkills, fields: sidebarFields, multiple: false, value: selectedSkillID, onUpdateValue: value => {
                setSelectedSkillID(value);
            }, view: skillsSblgView, onUpdateView: view => {
                setSkillsSblgView(prev => ({ ...prev, ...view }));
            }, sidebarWidth: {
                defaultWidth: 288,
                minWidth: 200,
                maxWidth: 420,
            }, listGridSettings: {
                defaultSortingKey: 'title',
            }, stubMessage: { lbl: 'No skills yet' }, adding: {
                onClick: () => {
                    openCreateSkillFlyout();
                },
            }, buildForm: () => (React.createElement("div", { className: "h-full min-h-0 flex flex-col" },
                React.createElement("div", { className: "shrink-0 border-b border-neutral-200 bg-white" },
                    React.createElement(ToolbarNew, { className: "[&_.side]:flex-nowrap", lhs: [
                            {
                                type: 'raw-jsx',
                                width: 36,
                                content: (React.createElement("div", { className: "w-9 h-8 flex items-center justify-center" },
                                    React.createElement(UsageLimitDonut, { value: estimatedTokenCount, max: SKILL_TOKEN_CAP, tiers: skillTokenTiers, curve: input => input.max > 0
                                            ? input.value / input.max
                                            : 0, tooltipContent: React.createElement("div", { style: {
                                                fontSize: '13px',
                                                lineHeight: '1.2',
                                                textAlign: 'left',
                                            } }, `Skill usage: ${estimatedTokenCount.toLocaleString()} / ${SKILL_TOKEN_CAP.toLocaleString()} tokens`), size: 16, thickness: 4, className: "cursor-help" }))),
                                overflowOverride: {
                                    label: 'Skill usage',
                                    labelDesc: `Skill usage: ${estimatedTokenCount.toLocaleString()} / ${SKILL_TOKEN_CAP.toLocaleString()} tokens`,
                                },
                            },
                        ], rhs: [
                            skillsFormMode === 'read'
                                ? {
                                    key: 'skills-edit',
                                    type: 'button-24',
                                    img: '/static/img/i8/color-edit.svg',
                                    lbl: 'Edit',
                                    classNameImg: 'w-4 h-4 my-[7px] mr-1.5',
                                    classNameSpan: 'whitespace-nowrap',
                                    title: 'Edit',
                                    width: 74,
                                    disabled: !selectedSkill ||
                                        Boolean(isGlobalReadOnly),
                                    onClick: () => {
                                        setSkillsFormMode('edit');
                                        requestAnimationFrame(() => {
                                            fullSkillTextareaRef.current =
                                                skillsEditFormRef.current?.querySelector('.js-skill-full-surface textarea') ?? null;
                                            fullSkillTextareaRef.current?.focus();
                                        });
                                    },
                                }
                                : {
                                    key: 'skills-save',
                                    type: 'button-24',
                                    img: '/static/img/i8/ios16-tick.svg',
                                    lbl: 'Save',
                                    classNameImg: 'w-4 h-4 my-[7px] mr-1.5',
                                    classNameSpan: 'whitespace-nowrap',
                                    title: 'Save',
                                    width: 76,
                                    disabled: skillsFormLoading != null ||
                                        !selectedSkill ||
                                        Boolean(isGlobalReadOnly),
                                    onClick: saveSkillFromToolbar,
                                },
                            skillsFormMode === 'read'
                                ? {
                                    key: 'skills-kebab',
                                    type: 'button-24',
                                    img: '/static/img/i8/ios16-bullet-list.svg',
                                    className: 'px-1.5',
                                    classNameImg: 'w-4 h-4 my-[7px] mr-0',
                                    title: 'More actions',
                                    width: 28,
                                    onClick: openSkillsToolbarMenu,
                                }
                                : null,
                            skillsFormMode === 'edit'
                                ? {
                                    key: 'skills-cancel',
                                    type: 'button-24',
                                    img: '/static/img/i8/ios16-refresh.svg',
                                    lbl: 'Cancel',
                                    classNameImg: 'w-4 h-4 my-[7px] mr-1.5',
                                    classNameSpan: 'whitespace-nowrap',
                                    title: 'Cancel',
                                    width: 80,
                                    disabled: skillsFormLoading != null,
                                    onClick: cancelSkillEditFromToolbar,
                                }
                                : null,
                        ] }),
                    skillsFormMessage ? (React.createElement("div", { className: "px-4 pb-2 text-sm text-red-600 whitespace-pre-wrap" }, skillsFormMessage)) : null),
                skillsFormMode === 'edit' ? (React.createElement("div", { ref: skillsEditFormRef, className: "flex-1 min-h-0 flex flex-col" },
                    React.createElement(FormComponent, { key: `skill-form-${selectedSkillID ?? 'none'}`, className: "h-full min-h-0 flex flex-col overflow-hidden", fields: skillFormFields, formPrompt: "Managing skills for agent automation", lblWidth: 100, defaultValue: () => selectedSkillFormValue, value: skillFormValue, disableFormFill: true, excludeButtons: true, disabled: Boolean(isGlobalReadOnly), onUpdate: setSkillFormValue, onSave: (_model, resolve) => {
                            resolve(true);
                        } }))) : (React.createElement("div", { className: "flex-1 overflow-auto p-3" },
                    showSystemSkillReadNotice ? (React.createElement("div", { className: "mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" }, "This is a system-level skill and is not visible to external users.")) : null,
                    React.createElement("div", { className: "w-full px-1 py-1", style: markdownWrapperStyle },
                        React.createElement("div", { className: "text-left select-text [&_h1]:!h-[28px] [&_h1]:!leading-[28px] [&_h1]:!text-[24px]" },
                            React.createElement(MarkdownRenderer, { content: composedSkillMarkdown, largeText: true }))))))) })));
};
const TaskDetailPane = ({ rs, taskID, taskDetails, }) => {
    const props = rs.props;
    const checklist = props.data.taskChecklists[taskID] ?? [];
    const attachments = props.data.taskAttachments[taskID] ?? [];
    const discussionItems = props.data.taskDiscussionItems[taskID] ?? [];
    const taskThreads = _.pick(props.data.agentThreads, taskDetails.ThreadIDs ?? []);
    const topLevelTaskThreads = _.pickBy(taskThreads, thread => !thread.metadata.parentThread);
    const childThreadsByParent = _.chain(taskThreads)
        .values()
        .filter(thread => Boolean(thread.metadata.parentThread))
        .groupBy(thread => thread.metadata.parentThread ?? '')
        .value();
    const hasActiveCategoriserThread = _.some(topLevelTaskThreads, thread => thread.metadata.status === 'active' &&
        thread.metadata.agentType.endsWith('/categoriser'));
    const isRunningCategoriser = rs.state.runningCategoriserTaskIDs.includes(taskID);
    const taskActionError = rs.state.taskActionErrors[taskID];
    const allTaskPendingChanges = _.chain(taskThreads)
        .flatMap(t => t.pendingChanges)
        .value();
    const selectedAttachmentID = rs.state.currentTab.startsWith('attachment:')
        ? Number(rs.state.currentTab.slice('attachment:'.length))
        : null;
    const selectedThreadID = rs.state.currentTab.startsWith('thread:')
        ? rs.state.currentTab.slice('thread:'.length)
        : null;
    const openEditTitleFlyout = () => {
        openFlyoutForm({
            title: 'Edit Task Title',
            size: [480, 180],
            lblWidth: 70,
            formPrompt: 'Update the task title',
            defaultValue: () => ({
                title: taskDetails.Title ?? '',
            }),
            fields: {
                title: FormType.Text({
                    lbl: 'Title',
                    maxLength: 80,
                    validators: [
                        {
                            req: (value) => value.trim().length > 0,
                            msg: 'is required',
                        },
                    ],
                }),
            },
            onSave: (model, resolve) => {
                props.taskCallbacks.onUpdateTask({
                    taskID,
                    updates: { Title: model.current.title.trim() },
                    callbacks: {
                        onComplete: () => {
                            resolve(true);
                        },
                        onError: error => {
                            resolve(error);
                        },
                    },
                });
            },
        });
    };
    const registerUploadedAttachments = (uploadedItems, resolve) => {
        const taskFiles = uploadedItems.map(item => ({
            guid: item.FileGuid ?? '',
            filename: item.Filename,
            mimeType: item.MimeType || 'application/octet-stream',
        }));
        if (taskFiles.some(item => item.guid.length === 0)) {
            resolve('Attachment missing GUID');
            return;
        }
        runPromisesSequentially(taskFiles.map(item => () => new Promise((done, reject) => {
            props.attachmentCallbacks.onAddAttachment({
                taskID,
                file: new File([], item.filename, { type: item.mimeType }),
                fileGuid: item.guid,
                callbacks: {
                    onComplete: () => {
                        done();
                    },
                    onError: error => {
                        reject(new Error(error));
                    },
                },
            });
        })))
            .then(() => {
            resolve(true);
        })
            .catch(error => {
            resolve(error instanceof Error
                ? `Failed to register attachments: ${error.message}`
                : `Failed to register attachments: ${String(error)}`);
        });
    };
    const openAddAttachmentsFlyout = () => {
        openFlyoutForm({
            title: 'Add Attachments',
            size: [520, 320],
            lblWidth: 100,
            formPrompt: 'Upload files to attach to this task',
            defaultValue: () => ({
                attachments: [],
            }),
            fields: {
                attachments: FormType.FileUploadMultiple({
                    lbl: 'Attachments',
                    height: 160,
                    fullWidth: true,
                    storageMode: 'v3',
                }),
            },
            onSave: (model, resolve) => {
                registerUploadedAttachments(model.current.attachments, resolve);
            },
        });
    };
    const runCategoriser = () => {
        if (rs.state.runningCategoriserTaskIDs.includes(taskID)) {
            return;
        }
        rs.updateState(state => ({
            currentTab: 'threads',
            taskActionErrors: _.omit(state.taskActionErrors, taskID),
            runningCategoriserTaskIDs: _.uniq([
                ...state.runningCategoriserTaskIDs,
                taskID,
            ]),
        }));
        props.taskCallbacks.onRunCategoriser({
            taskID,
            callbacks: {
                onComplete: () => {
                    rs.updateState(state => ({
                        taskActionErrors: _.omit(state.taskActionErrors, taskID),
                        runningCategoriserTaskIDs: state.runningCategoriserTaskIDs.filter(id => id !== taskID),
                    }));
                },
                onError: error => {
                    console.error('Failed to run categoriser:', error);
                    rs.updateState(state => ({
                        taskActionErrors: {
                            ...state.taskActionErrors,
                            [taskID]: error,
                        },
                        runningCategoriserTaskIDs: state.runningCategoriserTaskIDs.filter(id => id !== taskID),
                    }));
                },
            },
        });
    };
    const renderCurrentPane = () => {
        if (rs.state.currentTab === 'overview') {
            return (React.createElement(TaskOverviewPane, { taskDetails: taskDetails, checklist: checklist, attachments: attachments, discussionItems: discussionItems, taskThreads: taskThreads, pendingChanges: allTaskPendingChanges }));
        }
        if (rs.state.currentTab === 'review') {
            return (React.createElement(ReviewTab, { rs: rs, taskThreads: taskThreads, allPendingChanges: allTaskPendingChanges }));
        }
        if (rs.state.currentTab === 'task-details') {
            return (React.createElement(TaskNotePane, { rs: rs, taskID: taskID, title: "Task Details", value: taskDetails.PrimaryNote, field: "PrimaryNote" }));
        }
        if (rs.state.currentTab === 'agent-scratchpad') {
            return (React.createElement(TaskNotePane, { rs: rs, taskID: taskID, title: "Agent Scratchpad", value: taskDetails.SecondaryNote ?? '', field: "SecondaryNote" }));
        }
        if (rs.state.currentTab === 'discussion') {
            return (React.createElement(DiscussionTab, { rs: rs, taskID: taskID, discussionItems: discussionItems, attachments: attachments }));
        }
        if (rs.state.currentTab === 'attachments' ||
            Number.isFinite(selectedAttachmentID)) {
            return (React.createElement(TaskAttachmentsPane, { rs: rs, attachments: attachments, selectedAttachmentID: selectedAttachmentID }));
        }
        if (selectedThreadID && taskThreads[selectedThreadID]) {
            return (React.createElement("div", { className: "h-full overflow-hidden" },
                React.createElement(AgentThreadPanel, { rs: rs, thread: taskThreads[selectedThreadID], childThreads: childThreadsByParent[selectedThreadID] ?? [] })));
        }
        return (React.createElement(AgentsTab, { rs: rs, taskThreads: topLevelTaskThreads, childThreadsByParent: childThreadsByParent }));
    };
    return (React.createElement("div", { className: "h-full flex flex-col" },
        React.createElement(ToolbarNew, { lhs: [
                {
                    type: 'raw-jsx',
                    width: 520,
                    importance: 10,
                    content: (React.createElement(TaskTitleToolbarLabel, { taskDetails: taskDetails, onEditTitle: openEditTitleFlyout })),
                    overflowOverride: {
                        label: taskDetails.Title ?? 'Untitled Task',
                        onClick: openEditTitleFlyout,
                    },
                },
            ], rhs: [
                {
                    type: 'button-add',
                    lbl: 'Attachment',
                    width: 105,
                    importance: 8,
                    onClick: openAddAttachmentsFlyout,
                },
                !hasActiveCategoriserThread
                    ? {
                        type: 'button',
                        lbl: isRunningCategoriser
                            ? 'Categorising...'
                            : 'Categorise',
                        img: '/static/img/i8/w11c-bot.svg',
                        width: isRunningCategoriser ? 126 : 104,
                        importance: 9,
                        disabled: isRunningCategoriser,
                        onClick: runCategoriser,
                    }
                    : null,
            ] }),
        taskActionError && (React.createElement("div", { className: "border-b border-red-200 px-3 py-1 text-xs text-red-700" }, taskActionError)),
        React.createElement("div", { className: "min-h-0 flex-1" },
            React.createElement(ResizeSplitter, { value: rs.state.taskSplitWidths, onUpdate: value => {
                    rs.updateState({ taskSplitWidths: value });
                }, panes: [
                    {
                        defaultWidth: 230,
                        minWidth: 180,
                        maxWidth: 360,
                        collapsible: false,
                        flexWidth: null,
                        content: (React.createElement(TaskLocalNav, { rs: rs, attachments: attachments, taskThreads: topLevelTaskThreads, pendingChangesCount: allTaskPendingChanges.length })),
                    },
                    {
                        defaultWidth: null,
                        minWidth: null,
                        maxWidth: null,
                        collapsible: false,
                        flexWidth: 1,
                        content: (React.createElement("div", { className: "h-full min-w-0" }, renderCurrentPane())),
                    },
                ] }))));
};
const TaskTitleToolbarLabel = (props) => (React.createElement("div", { className: "group flex h-full min-w-0 items-center gap-2 px-2", onDoubleClick: props.onEditTitle },
    React.createElement("span", { className: BuildClass({
            'shrink-0 rounded px-2 py-0.5 text-xs': true,
            'bg-green-100 text-green-700': props.taskDetails.Status === 900,
            'bg-blue-100 text-blue-700': props.taskDetails.Status >= 100 && props.taskDetails.Status < 200,
            'bg-yellow-100 text-yellow-700': props.taskDetails.Status >= 200 && props.taskDetails.Status < 900,
            'bg-red-100 text-red-700': props.taskDetails.Status === 80 ||
                (props.taskDetails.Status >= 601 && props.taskDetails.Status <= 603),
            'bg-neutral-100 text-neutral-700': props.taskDetails.Status === 50,
        }) }, STATUS_LABELS[props.taskDetails.Status] ?? 'Unknown'),
    React.createElement("span", { className: "min-w-0 truncate text-base font-medium" }, props.taskDetails.Title ?? 'Untitled Task'),
    React.createElement("button", { type: "button", title: "Edit task title", className: "shrink-0 border-0 bg-transparent p-0 opacity-0 group-hover:opacity-70", onClick: props.onEditTitle },
        React.createElement("img", { src: "/static/img/i8/ios16-pencil.svg", alt: "", className: "h-4 w-4", style: { width: 16, height: 16 } }))));
const TaskLocalNav = ({ rs, attachments, taskThreads, pendingChangesCount, }) => {
    const rows = [
        {
            value: 'overview',
            label: 'Overview',
            icon: 'overview',
        },
        {
            value: 'review',
            label: pendingChangesCount > 0
                ? `Review Changes (${pendingChangesCount})`
                : 'Review Changes',
            icon: 'review',
        },
        {
            value: 'task-details',
            label: 'Task Details',
            icon: 'task-details',
        },
        {
            value: 'agent-scratchpad',
            label: 'Agent Scratchpad',
            icon: 'scratchpad',
        },
        {
            value: 'discussion',
            label: 'Discussion',
            icon: 'discussion',
        },
        {
            value: 'attachments',
            label: `Attachments (${attachments.length})`,
            icon: 'attachments',
            selectable: false,
        },
        ...attachments.map(attachment => ({
            value: `attachment:${attachment.ID}`,
            label: attachment.Filename ?? attachment.FileGUID,
            searchText: [attachment.Filename, attachment.FileGUID, attachment.MimeType]
                .filter(Boolean)
                .join(' '),
            icon: getTaskAttachmentIcon(attachment),
            indent: true,
        })),
        {
            value: 'threads',
            label: `Threads (${_.size(taskThreads)})`,
            icon: 'thread-categoriser',
            selectable: false,
        },
        ..._.values(taskThreads).map(thread => ({
            value: `thread:${thread.metadata.id}`,
            label: thread.metadata.title ??
                thread.firstMessagePreview ??
                thread.metadata.agentType,
            searchText: [
                thread.metadata.title,
                thread.firstMessagePreview,
                thread.metadata.agentType,
            ]
                .filter(Boolean)
                .join(' '),
            icon: getAgentThreadIcon(thread.metadata.agentType),
            indent: true,
        })),
    ];
    return (React.createElement("div", { className: "h-full" },
        React.createElement(List, { className: "h-full", value: rs.state.currentTab, onUpdate: value => {
                if (value) {
                    rs.updateState({ currentTab: value });
                }
            }, height: 30, multiple: false, rows: rows.map(row => ({
                value: row.value,
                height: 30,
                selectable: row.selectable ?? true,
                className: row.selectable === false
                    ? 'text-neutral-500'
                    : 'hover:bg-yellow-100 cursor-pointer',
                content: () => (React.createElement("div", { className: BuildClass([
                        'flex h-[30px] items-center gap-2 px-2',
                        row.indent ? 'pl-7' : '',
                    ]), title: row.label },
                    React.createElement(AgentConsoleIcon, { name: row.icon, size: 18, className: "shrink-0" }),
                    React.createElement("span", { className: "min-w-0 flex-1 truncate" }, row.label))),
            })) })));
};
const TaskOverviewPane = ({ taskDetails, checklist, attachments, discussionItems, taskThreads, pendingChanges, }) => (React.createElement("div", { className: "h-full overflow-y-auto p-4" },
    React.createElement("div", { className: "grid max-w-5xl grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3" },
        React.createElement(TaskMetric, { label: "Status", value: STATUS_LABELS[taskDetails.Status] ?? 'Unknown' }),
        React.createElement(TaskMetric, { label: "Attachments", value: String(attachments.length) }),
        React.createElement(TaskMetric, { label: "Threads", value: String(_.size(taskThreads)) }),
        React.createElement(TaskMetric, { label: "Pending Changes", value: String(pendingChanges.length) })),
    React.createElement("div", { className: "mt-4 grid max-w-5xl grid-cols-2 gap-4" },
        React.createElement("div", { className: "rounded border border-neutral-200 bg-white p-3" },
            React.createElement("h3", { className: "mb-2 text-sm font-medium" }, "Task Details"),
            React.createElement("div", { className: "max-h-56 overflow-hidden text-sm" }, taskDetails.PrimaryNote ? (React.createElement(MarkdownRenderer, { content: taskDetails.PrimaryNote })) : (React.createElement("span", { className: "text-neutral-400" }, "No task details")))),
        React.createElement("div", { className: "rounded border border-neutral-200 bg-white p-3" },
            React.createElement("h3", { className: "mb-2 text-sm font-medium" }, "Agent Scratchpad"),
            React.createElement("div", { className: "max-h-56 overflow-hidden text-sm" }, taskDetails.SecondaryNote ? (React.createElement(MarkdownRenderer, { content: taskDetails.SecondaryNote })) : (React.createElement("span", { className: "text-neutral-400" }, "No scratchpad notes")))),
        React.createElement("div", { className: "rounded border border-neutral-200 bg-white p-3" },
            React.createElement("h3", { className: "mb-2 text-sm font-medium" }, "Checklist"),
            React.createElement("div", { className: "space-y-1 text-sm" }, checklist.length > 0 ? (checklist.slice(0, 6).map(item => (React.createElement("div", { key: item.ID, className: "truncate" },
                item.VerifiedCompleteAt ? '✓ ' : '□ ',
                item.ItemName)))) : (React.createElement("span", { className: "text-neutral-400" }, "No checklist items")))),
        React.createElement("div", { className: "rounded border border-neutral-200 bg-white p-3" },
            React.createElement("h3", { className: "mb-2 text-sm font-medium" }, "Discussion"),
            React.createElement("div", { className: "text-sm text-neutral-600" }, discussionItems.length === 0
                ? 'No human comments yet'
                : `${discussionItems.length} human comment${discussionItems.length === 1 ? '' : 's'}`)))));
const TaskMetric = (props) => (React.createElement("div", { className: "rounded border border-neutral-200 bg-white px-3 py-2" },
    React.createElement("div", { className: "text-xs text-neutral-500" }, props.label),
    React.createElement("div", { className: "mt-1 truncate text-sm font-medium" }, props.value)));
const TaskNotePane = ({ rs, taskID, title, value, field, }) => {
    const draftKey = field === 'PrimaryNote' ? 'draftPrimaryNote' : 'draftSecondaryNote';
    const draftValue = rs.state[draftKey];
    const placeholder = field === 'PrimaryNote'
        ? 'Describe what needs to be done, including any important context, constraints, or acceptance details.'
        : 'Add working notes for agents, assumptions, partial findings, or anything that should inform later task work.';
    React.useEffect(() => {
        rs.updateState({
            [draftKey]: value,
        });
    }, [draftKey, value, rs]);
    const saveNote = () => {
        rs.props.taskCallbacks.onUpdateTask({
            taskID,
            updates: { [field]: draftValue },
            callbacks: {
                onComplete: () => { },
                onError: error => {
                    console.error('Failed to save task note:', error);
                },
            },
        });
    };
    return (React.createElement("div", { className: "flex h-full flex-col" },
        React.createElement(ToolbarNew, { className: "border-b border-neutral-200", lhs: [{ type: 'label', lbl: title, width: 180 }], rhs: [
                {
                    type: 'button',
                    lbl: 'Reset',
                    img: '/static/img/i8/ios16-refresh.svg',
                    width: 82,
                    onClick: () => {
                        rs.updateState({ [draftKey]: value });
                    },
                },
                {
                    type: 'button',
                    lbl: 'Save',
                    img: '/static/img/i8/material-outline-checkmark.svg',
                    width: 76,
                    onClick: saveNote,
                },
            ] }),
        React.createElement("div", { className: "min-h-0 flex-1 p-1" },
            React.createElement(TextareaWithVoice, { value: draftValue, onUpdate: text => {
                    rs.updateState({ [draftKey]: text });
                }, prompt: null, maxLength: TASK_NOTE_MAX_LENGTH, placeholder: placeholder, className: "h-full w-full", classNameTextarea: "border-0 p-1 pb-6 focus:outline-none" }))));
};
const TaskAttachmentsPane = ({ rs, attachments, selectedAttachmentID, }) => {
    const props = rs.props;
    const selectedAttachment = selectedAttachmentID == null
        ? null
        : (attachments.find(a => a.ID === selectedAttachmentID) ?? null);
    const confirmDeleteAttachment = (attachment) => {
        PromptConfirm({
            title: 'Remove attachment?',
            msg: `Remove ${attachment.Filename ?? attachment.FileGUID} from this task?`,
            size: [360, 190],
            yes: done => {
                props.attachmentCallbacks.onDeleteAttachment({
                    attachmentID: attachment.ID,
                    callbacks: {
                        onComplete: () => {
                            if (selectedAttachmentID === attachment.ID) {
                                rs.updateState({ currentTab: 'attachments' });
                            }
                            done(true);
                        },
                        onError: err => {
                            done(err);
                        },
                    },
                });
            },
        });
    };
    const attachmentTitle = selectedAttachment?.Title ??
        selectedAttachment?.Filename ??
        selectedAttachment?.FileGUID ??
        'Attachment';
    const attachmentType = _.last(selectedAttachment?.MimeType.split('/') ?? []) ??
        selectedAttachment?.MimeType ??
        '';
    const isDisabled = Boolean(selectedAttachment?.DisabledAt);
    const [titleDraft, setTitleDraft] = React.useState('');
    const [detailsDraft, setDetailsDraft] = React.useState('');
    React.useEffect(() => {
        setTitleDraft(selectedAttachment?.Title ?? '');
        setDetailsDraft(selectedAttachment?.Details ?? '');
    }, [selectedAttachment?.ID]);
    const saveAttachmentDetails = () => {
        if (!selectedAttachment) {
            return;
        }
        props.attachmentCallbacks.onUpdateAttachment({
            attachmentID: selectedAttachment.ID,
            updates: {
                Title: titleDraft.trim() || null,
                Details: detailsDraft.trim() || null,
            },
            callbacks: {
                onComplete: () => { },
                onError: error => {
                    console.error('Failed to save attachment details:', error);
                },
            },
        });
    };
    const toggleAttachmentDisabled = () => {
        if (!selectedAttachment) {
            return;
        }
        props.attachmentCallbacks.onUpdateAttachment({
            attachmentID: selectedAttachment.ID,
            updates: isDisabled
                ? {
                    DisabledAt: null,
                    DisabledReason: null,
                    DisabledSourceThreadID: null,
                }
                : {
                    DisabledAt: DateTimeObj.now().fmt(),
                    DisabledBy: props.currentUserID,
                    DisabledReason: 'Disabled by user',
                    DisabledSourceThreadID: null,
                },
            callbacks: {
                onComplete: () => { },
                onError: error => {
                    console.error('Failed to update attachment status:', error);
                },
            },
        });
    };
    return (React.createElement("div", { className: "flex h-full flex-col" },
        React.createElement(ToolbarNew, { className: "border-b border-neutral-200", lhs: [{ type: 'label', lbl: 'Attachments', width: 120 }], rhs: [
                ...(selectedAttachment
                    ? [
                        {
                            type: 'button',
                            lbl: isDisabled ? 'Enable' : 'Disable',
                            img: isDisabled
                                ? '/static/img/i8/material-outline-eye.svg'
                                : '/static/img/i8/material-outline-closed-eye.svg',
                            width: 92,
                            onClick: toggleAttachmentDisabled,
                        },
                        {
                            type: 'button',
                            lbl: 'Remove',
                            img: '/static/img/i8/material-outline-delete.svg',
                            width: 88,
                            onClick: () => {
                                confirmDeleteAttachment(selectedAttachment);
                            },
                        },
                    ]
                    : []),
            ] }),
        React.createElement("div", { className: "min-h-0 flex-1" }, attachments.length === 0 ? (React.createElement("div", { className: "flex h-full items-center justify-center text-sm text-neutral-500" }, "No attachments")) : !selectedAttachment ? (React.createElement("div", { className: "flex h-full items-center justify-center text-sm text-neutral-500" }, "Select an attachment")) : (React.createElement(ResizeSplitter, { value: rs.state.attachmentSplitWidths, onUpdate: value => {
                rs.updateState({ attachmentSplitWidths: value });
            }, panes: [
                {
                    defaultWidth: 300,
                    minWidth: 240,
                    maxWidth: 460,
                    collapsible: false,
                    flexWidth: null,
                    content: (React.createElement("div", { className: "flex h-full flex-col gap-2 p-2" },
                        React.createElement("div", null,
                            React.createElement("div", { className: "mb-1 text-xs text-neutral-500" }, "Title"),
                            React.createElement(Textbox, { value: titleDraft, onUpdate: setTitleDraft, placeholder: attachmentTitle })),
                        React.createElement("div", { className: "min-h-0 flex-1" },
                            React.createElement("div", { className: "mb-1 text-xs text-neutral-500" }, "Description"),
                            React.createElement(TextareaWithVoice, { value: detailsDraft, onUpdate: setDetailsDraft, prompt: null, maxLength: TASK_NOTE_MAX_LENGTH, placeholder: "Summarise what this attachment contains and why it matters for the task.", className: "h-[calc(100%-20px)] w-full", classNameTextarea: "border-0 p-1 pb-6 focus:outline-none" })),
                        React.createElement("div", { className: "text-xs text-neutral-500" },
                            attachmentType,
                            isDisabled ? ' • Disabled' : ''),
                        React.createElement(ToolbarNew, { rhs: [
                                {
                                    type: 'button',
                                    lbl: 'Reset',
                                    img: '/static/img/i8/ios16-refresh.svg',
                                    width: 82,
                                    onClick: () => {
                                        setTitleDraft(selectedAttachment.Title ??
                                            '');
                                        setDetailsDraft(selectedAttachment.Details ??
                                            '');
                                    },
                                },
                                {
                                    type: 'button',
                                    lbl: 'Save',
                                    img: '/static/img/i8/material-outline-checkmark.svg',
                                    width: 76,
                                    onClick: saveAttachmentDetails,
                                },
                            ] }))),
                },
                {
                    defaultWidth: null,
                    minWidth: null,
                    maxWidth: null,
                    collapsible: false,
                    flexWidth: 1,
                    content: (React.createElement(NoteAttachment, { attachment: {
                            Checksum: null,
                            FileGUID: selectedAttachment.FileGUID,
                            MimeType: selectedAttachment.MimeType,
                        } })),
                },
            ] })))));
};
const DiscussionTab = ({ rs, taskID, discussionItems, attachments, }) => {
    const props = rs.props;
    const discussionThreadID = 1;
    const attachmentsByGuid = _.keyBy(attachments, 'FileGUID');
    const discussionItemsByID = _.keyBy(discussionItems, 'ID');
    return (React.createElement("div", { className: "h-full flex flex-col" },
        React.createElement(ToolbarNew, { className: "border-b border-neutral-200", lhs: [{ type: 'label', lbl: 'Team Discussion', width: 160 }], rhs: [
                {
                    type: 'button-24',
                    img: '/static/img/i8/ios16-info.svg',
                    title: 'Collaboration agents can read the discussion on request. It is not used when completing a task unless copied into Task Details, Agent Scratchpad, or an attachment summary.',
                    width: 30,
                    onClick: () => { },
                },
            ] }),
        React.createElement("div", { className: "min-h-0 flex-1" },
            React.createElement(ChatBasic, { facility: props.facility, userID: props.currentUserID, contextID: discussionThreadID, contextScopeKey: `agent-task-discussion-${taskID}`, targetContextDisplayName: "Team", hideThreadList: true, hideThreadToolbar: true, requestLoadThreads: handlers => {
                    handlers.onSuccess({
                        threads: [
                            {
                                ID: discussionThreadID,
                                Title: 'Team Discussion',
                                CreatedAt: _.first(discussionItems)?.Timestamp ?? null,
                                ResolvedAt: null,
                            },
                        ],
                        messages: discussionItems.map(item => ({
                            ID: item.ID,
                            Thread: discussionThreadID,
                            Text: item.Text,
                            CreatedAt: item.Timestamp,
                            AuthorUserID: item.Author,
                            AuthorContextID: null,
                            IsPrivileged: false,
                            VisibleToTarget: true,
                        })),
                        canControlVisibility: false,
                    });
                    handlers.onDone();
                }, requestCreateThreadWithInitialMessage: (input, handlers) => {
                    props.discussionCallbacks.onPostDiscussion({
                        taskID,
                        text: input.initialMessage,
                        callbacks: {
                            onComplete: item => {
                                handlers.onSuccess({
                                    thread: {
                                        ID: discussionThreadID,
                                        Title: 'Team Discussion',
                                        CreatedAt: item.Timestamp,
                                        ResolvedAt: null,
                                    },
                                    chatMessage: {
                                        ID: item.ID,
                                        Thread: discussionThreadID,
                                        Text: item.Text,
                                        CreatedAt: item.Timestamp,
                                        AuthorUserID: item.Author,
                                        AuthorContextID: null,
                                        IsPrivileged: false,
                                        VisibleToTarget: true,
                                    },
                                });
                                handlers.onDone();
                            },
                            onError: error => {
                                handlers.onError(error);
                                handlers.onDone();
                            },
                        },
                    });
                }, requestSendMessage: (input, handlers) => {
                    props.discussionCallbacks.onPostDiscussion({
                        taskID,
                        text: input.text,
                        callbacks: {
                            onComplete: item => {
                                handlers.onSuccess({
                                    chatMessage: {
                                        ID: item.ID,
                                        Thread: discussionThreadID,
                                        Text: item.Text,
                                        CreatedAt: item.Timestamp,
                                        AuthorUserID: item.Author,
                                        AuthorContextID: null,
                                        IsPrivileged: false,
                                        VisibleToTarget: true,
                                    },
                                });
                                handlers.onDone();
                            },
                            onError: error => {
                                handlers.onError(error);
                                handlers.onDone();
                            },
                        },
                    });
                }, requestUpdateThread: (_, handlers) => {
                    handlers.onSuccess({ thread: null });
                    handlers.onDone();
                }, renderMessageBlips: input => {
                    const fileGuid = discussionItemsByID[input.message.ID]?.FileGuid;
                    if (!fileGuid) {
                        return null;
                    }
                    const attachment = attachmentsByGuid[fileGuid];
                    return (React.createElement("span", { className: "inline-flex rounded bg-neutral-100 px-2 py-0.5 text-xs text-neutral-600" }, attachment?.Filename ?? fileGuid));
                } }))));
};
const AgentThreadPanel = ({ rs, thread, childThreads, child, }) => {
    const props = rs.props;
    return (React.createElement("div", { className: BuildClass({
            'flex h-full flex-col overflow-hidden': true,
            'rounded border border-neutral-200': child ?? false,
            'ml-6': child ?? false,
        }) },
        React.createElement("div", { className: "border-b border-neutral-200 bg-neutral-50 p-2" },
            React.createElement("span", { className: "text-sm font-medium" }, thread.metadata.title ??
                thread.firstMessagePreview ??
                'Agent Thread'),
            React.createElement("span", { className: "ml-2 text-xs text-neutral-500" }, thread.metadata.agentType)),
        React.createElement("div", { className: child ? 'h-[320px]' : 'min-h-0 flex-1' },
            React.createElement(AgentUI, { threads: { [thread.metadata.id]: thread }, currentUserID: props.currentUserID, facility: props.facility, subFacility: props.subFacility ?? 0, agentType: thread.metadata.agentType, changeRenderers: props.changeRenderers, widgetToGroupCode: props.widgetToGroupCode, toolRenderers: props.toolRenderers, toolHandlers: props.toolHandlers, theme: props.theme, hue: props.hue, isReadOnly: true, hideSidebar: true, readOnlyMessage: "This thread is read-only. Use Discussion for human updates.", onSendMessage: () => { }, onApproveChanges: () => { }, onRejectChanges: () => { }, onLoadThreadContent: props.agentThreadCallbacks.onLoadThreadContent })),
        (childThreads ?? []).length > 0 && (React.createElement("div", { className: "space-y-3 border-t border-neutral-200 bg-neutral-50/40 p-3" }, (childThreads ?? []).map(childThread => (React.createElement(AgentThreadPanel, { key: childThread.metadata.id, rs: rs, thread: childThread, child: true })))))));
};
const AgentsTab = ({ rs, taskThreads, childThreadsByParent, }) => {
    const threads = _.values(taskThreads);
    const orphanChildThreads = _.chain(childThreadsByParent)
        .toPairs()
        .filter(([parentThreadID]) => !taskThreads[parentThreadID])
        .flatMap(([, children]) => children)
        .value();
    if (threads.length === 0 && orphanChildThreads.length === 0) {
        return (React.createElement("div", { className: "h-full flex items-center justify-center text-sm text-neutral-500" }, "No agent threads for this task"));
    }
    return (React.createElement("div", { className: "h-full overflow-y-auto" },
        React.createElement("div", { className: "space-y-4 p-3" },
            threads.map(thread => (React.createElement(AgentThreadPanel, { key: thread.metadata.id, rs: rs, thread: thread, childThreads: childThreadsByParent[thread.metadata.id] ?? [] }))),
            orphanChildThreads.map(child => (React.createElement(AgentThreadPanel, { key: child.metadata.id, rs: rs, thread: child, child: true }))))));
};
const ReviewTab = ({ rs, taskThreads, allPendingChanges, }) => {
    const props = rs.props;
    const isDarkMode = (props.theme ?? 'light') === 'dark';
    const changesWithStatus = allPendingChanges.map(c => ({
        ...c,
        status: rs.state.pendingChangesStates[c.id] ?? getPendingChangeStatus(c),
        createdAt: '',
    }));
    const executingIDs = new Set(changesWithStatus.filter(c => c.status === 'executing').map(c => c.id));
    const updateChangeStates = (updates) => {
        rs.updateState(s => ({
            pendingChangesStates: { ...s.pendingChangesStates, ...updates },
        }));
    };
    const updateChangeState = (id, status) => {
        updateChangeStates({ [id]: status });
    };
    const handleApprove = async (changeID) => {
        const thread = _.values(taskThreads).find(t => t.pendingChanges.some(pc => pc.id === changeID));
        if (!thread) {
            return;
        }
        updateChangeState(changeID, 'executing');
        try {
            await new Promise((resolve, reject) => {
                props.agentThreadCallbacks.onApproveChanges({
                    threadIDs: [thread.metadata.id],
                    approvalIDs: [changeID],
                    callbacks: {
                        onComplete: result => {
                            if (result.errors && result.errors.length > 0) {
                                updateChangeState(changeID, 'error');
                                reject(new Error(result.errors[0]?.error));
                            }
                            else {
                                updateChangeState(changeID, 'approved');
                                resolve();
                            }
                        },
                        onError: err => {
                            updateChangeState(changeID, 'error');
                            reject(new Error(err));
                        },
                    },
                });
            });
        }
        catch {
            updateChangeState(changeID, 'error');
        }
    };
    const handleReject = (changeID) => {
        updateChangeState(changeID, 'rejected');
        props.agentThreadCallbacks.onRejectChanges({
            approvalIDs: [changeID],
            callbacks: {
                onComplete: threads => {
                    updateChangeState(changeID, hasPendingApproval(threads, changeID) ? 'pending' : 'rejected');
                },
            },
        });
    };
    const handleApproveAll = async (changeIDs) => {
        const pending = changesWithStatus.filter(change => changeIDs.includes(change.id) && change.status === 'pending');
        if (pending.length === 0) {
            return;
        }
        updateChangeStates(_.chain(pending)
            .map(c => [c.id, 'executing'])
            .fromPairs()
            .value());
        const changesByThread = _.chain(pending)
            .map(c => {
            const thread = _.values(taskThreads).find(t => t.pendingChanges.some(pc => pc.id === c.id));
            return thread ? { changeID: c.id, threadID: thread.metadata.id } : null;
        })
            .compact()
            .groupBy('threadID')
            .value();
        await Promise.all(Object.entries(changesByThread).map(async ([threadID, changes]) => {
            const approvalIDs = changes.map(c => c.changeID);
            return new Promise((resolve, reject) => {
                props.agentThreadCallbacks.onApproveChanges({
                    threadIDs: [threadID],
                    approvalIDs,
                    callbacks: {
                        onComplete: result => {
                            if (result.errors && result.errors.length > 0) {
                                updateChangeStates(_.chain(approvalIDs)
                                    .map(id => [id, 'pending'])
                                    .fromPairs()
                                    .value());
                                reject(new Error('Some changes failed'));
                            }
                            else {
                                updateChangeStates(_.chain(approvalIDs)
                                    .map(id => [id, 'approved'])
                                    .fromPairs()
                                    .value());
                                resolve();
                            }
                        },
                        onError: () => {
                            updateChangeStates(_.chain(approvalIDs)
                                .map(id => [id, 'pending'])
                                .fromPairs()
                                .value());
                            reject(new Error('Approval failed'));
                        },
                    },
                });
            }).catch(() => {
            });
        }));
    };
    const handleRejectAll = (changeIDs) => {
        const pendingIDs = changesWithStatus
            .filter(change => changeIDs.includes(change.id) && change.status === 'pending')
            .map(c => c.id);
        _.forEach(pendingIDs, id => {
            handleReject(id);
        });
    };
    return (React.createElement("div", { className: "h-full flex flex-col" },
        React.createElement("div", { className: "flex-1 relative min-h-0" },
            React.createElement("div", { className: "absolute inset-0 pointer-events-none z-0", style: {
                    backgroundColor: isDarkMode
                        ? 'oklch(0 0 0 / 0.9)'
                        : 'oklch(0 0 0 / 0.1)',
                } }),
            React.createElement("div", { className: "absolute inset-0 pointer-events-none z-0", style: {
                    backgroundImage: 'url(/static/img/svg/window-background-texture.svg)',
                    backgroundRepeat: 'repeat',
                    backgroundSize: '5px',
                    filter: isDarkMode
                        ? 'hue-rotate(200deg) invert(1) brightness(0.9)'
                        : 'hue-rotate(260deg) grayscale(50%)',
                    opacity: isDarkMode ? 0.15 : 0.2,
                } }),
            React.createElement("div", { className: "absolute inset-0 overflow-y-scroll z-10 flex flex-col px-1" },
                React.createElement("div", { className: "w-full max-w-[800px] mx-auto pt-4 pb-4" }, changesWithStatus.length > 0 ? (React.createElement(PendingChangesTray, { changes: changesWithStatus, renderers: props.changeRenderers, toolHandlers: {}, onApprove: ids => {
                        if (ids.length === 1) {
                            const changeID = ids[0];
                            if (!changeID) {
                                return;
                            }
                            handleApprove(changeID).catch(console.error);
                            return;
                        }
                        handleApproveAll(ids).catch(console.error);
                    }, onReject: ids => {
                        if (ids.length === 1) {
                            const changeID = ids[0];
                            if (!changeID) {
                                return;
                            }
                            handleReject(changeID);
                            return;
                        }
                        handleRejectAll(ids);
                    }, executingIDs: executingIDs, disabled: false, widgetToGroupCode: props.widgetToGroupCode })) : (React.createElement("div", { className: "flex items-center justify-center py-8" },
                    React.createElement("p", { className: "text-sm text-neutral-500" }, "No pending changes for this task"))))))));
};
//# sourceMappingURL=index.js.map